import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import PublicPageOverlay from "../components/PublicPageOverlay";
import { Zap } from "lucide-react";
import { toast } from "sonner";
import ActionBlockRenderer from "../components/ActionBlockRenderer";
import HubEngagementBar from "../components/HubEngagementBar";
import SeoHead from "../components/SeoHead";
import { orderAdsLast, rememberQrVisit } from "../lib/quickPromote";
import "../styles/action-hub-themes.css";
import { bootFetch } from "../lib/bootFetch";
import { linksThemeStyle } from "../lib/linksThemeStyle";

/**
 * Public Action Page — linkola.app/u/:creator_slug/:page_slug
 *
 * Phase 2: renders themed blocks + fires anonymous visitor tracking.
 * A `ah_vid` cookie stores a stable visitor id for the CTR/unique-visitor
 * calculations on the author's analytics dashboard.
 *
 * v149.72 — An Action Page is designed like the Web Link, and a page that
 * has been through the designer (`page.layout === "blocks"`) is DRAWN like
 * the Web Link: the engagement bar, then its blocks, in the Web Link's
 * column — its photo, title and description are blocks the owner placed.
 * A page that hasn't keeps the layout it was built for, with the header
 * this page draws itself (photo, name, title, description).
 */

function getOrMintVisitorId() {
  try {
    const KEY = "creatoraiq.ah_vid";
    let v = localStorage.getItem(KEY);
    if (!v) {
      v = "vst_" + Math.random().toString(36).slice(2, 14) + Date.now().toString(36).slice(-4);
      localStorage.setItem(KEY, v);
    }
    return v;
  } catch {
    return "vst_ephemeral";
  }
}

export default function PublicActionPage() {
  const { creator_slug: creatorSlug, page_slug: pageSlug } = useParams();
  // v149.2 — Smart QR scans land with ?qr=CODE; remember it for ad attribution.
  useEffect(() => { rememberQrVisit(creatorSlug); }, [creatorSlug]);
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [gateUnlocked, setGateUnlocked] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const visitorId = useMemo(() => getOrMintVisitorId(), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // v149.64 — Often already on its way: index.html started it.
        const res = await bootFetch(
          `${backend}/api/public/pages/${encodeURIComponent(creatorSlug)}/${encodeURIComponent(pageSlug)}`,
        );
        if (!res.ok) {
          const detail = res.status === 404 ? "Page not found" : `Error ${res.status}`;
          if (!cancelled) setState({ loading: false, data: null, error: detail });
          return;
        }
        const json = await res.json();
        if (!cancelled) setState({ loading: false, data: json, error: null });

        // Fire visit tracking (fire-and-forget)
        fetch(
          `${backend}/api/public/pages/${encodeURIComponent(creatorSlug)}/${encodeURIComponent(pageSlug)}/visit`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              visitor_id: visitorId,
              referrer: document.referrer || null,
              ua: navigator.userAgent,
            }),
          },
        ).catch(() => {});
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: "Network error" });
      }
    })();
    return () => { cancelled = true; };
  }, [creatorSlug, pageSlug, backend, visitorId]);

  const trackClick = (block) => {
    if (!block?.block_id) return;
    fetch(
      `${backend}/api/public/pages/${encodeURIComponent(creatorSlug)}/${encodeURIComponent(pageSlug)}/click/${encodeURIComponent(block.block_id)}`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visitor_id: visitorId }),
      },
    ).catch(() => {});
  };

  // v89 (Feb 2026) — page_gate detection & unlock persistence.
  // Computed unconditionally so the hook order is stable across renders,
  // even when we early-return during loading / error states.
  const pageData = state.data?.page || null;
  // v149 — The Quick Promote ads block always renders last.
  const allBlocks = orderAdsLast(pageData?.blocks || []);
  const gateIdx = allBlocks.findIndex((b) => b?.type === "page_gate");
  const gateBlock = gateIdx >= 0 ? allBlocks[gateIdx] : null;
  const gateRemember = gateBlock ? (gateBlock.config?.remember !== false) : false;
  const gateKey = gateBlock && pageData
    ? `ah:gate:${pageData.page_id}:${gateBlock.block_id}`
    : null;

  useEffect(() => {
    if (!gateKey || !gateRemember) return;
    try {
      if (localStorage.getItem(gateKey)) setGateUnlocked(true);
    } catch { /* localStorage disabled */ }
  }, [gateKey, gateRemember]);

  const handleGateUnlock = () => {
    setGateUnlocked(true);
    if (gateKey && gateRemember) {
      try { localStorage.setItem(gateKey, new Date().toISOString()); } catch { /* noop */ }
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page" data-testid="public-page-loading">
        <p className="font-bold text-sm text-ink/70">Loading…</p>
      </div>
    );
  }

  if (state.error || !state.data?.page) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-page px-6 text-center" data-testid="public-page-404">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
          style={{ background: "color-mix(in srgb, var(--ux-coral) 15%, white)", border: "1px solid var(--ux-border)" }}
        >
          <Zap className="w-8 h-8" strokeWidth={2.5} style={{ color: "var(--ux-coral)" }} />
        </div>
        <h1 className="font-display font-black text-3xl tracking-tight mb-2">Page not found</h1>
        <p className="text-sm max-w-sm" style={{ color: "var(--ux-ink-2)" }}>
          The Action Page at <span className="font-mono">/u/{creatorSlug}/{pageSlug}</span> doesn&apos;t exist or hasn&apos;t been published yet.
        </p>
        <Link
          to="/"
          data-testid="public-page-home-btn"
          className="mt-6 ux-btn ux-btn-primary"
        >
          Go home
        </Link>
      </div>
    );
  }

  const { page, creator, products, settings, affiliate_products: affiliateProducts } = state.data;
  const theme = page.theme || "classic";
  const blockLayout = page.layout === "blocks";
  const linksStyle = linksThemeStyle(theme, page.theme_settings);

  // A coupons grid's "copy code": copy, and count the redemption (as the Web Link does).
  const redeemCoupon = async (coupon) => {
    try {
      await navigator.clipboard.writeText(coupon.code);
      toast.success(`Copied "${coupon.code}"`);
      fetch(`${backend}/api/public/coupons/${coupon.coupon_id}/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visitor_id: visitorId }),
      }).catch(() => {});
    } catch {
      toast.error("Couldn't copy — copy it manually");
    }
  };

  // v113 — Build a hubContext so any embedded `storefront` block on this
  // action page gets the SAME buy modal (services / coupons / OTP /
  // address / instructions) as /store/{creatorSlug}.
  const hubContext = {
    creator: { ...(creator || {}), slug: creatorSlug },
    slug: creatorSlug,
    // v149 — Lets the `ads` block request ads for this specific page.
    page_id: page.page_id,
    products: products || [],
    // v145.64 — Server-hydrated affiliate picks for the dedicated
    // `affiliate_picks` block. Regular products_grid / featured_product
    // blocks continue to read from `products` (creator's own catalog).
    affiliate_products: affiliateProducts || [],
    settings: settings || {},
    // v145.66 / v149.72 — The owner's other published pages and active
    // coupons, so Explore Pages and coupon grids work here as on the Web Link.
    pages: state.data.pages || [],
    coupons: state.data.coupons || [],
    pageHref: (p) => `/u/${creatorSlug}/${p.slug}`,
    onCouponClick: (_e, cp) => redeemCoupon(cp),
  };

  const visibleBlocks = gateBlock && !gateUnlocked
    ? allBlocks.slice(0, gateIdx + 1)
    : allBlocks;

  const blockList = allBlocks.length === 0 ? (
    <div
      className="border-2 border-dashed p-6 text-center bg-transparent"
      style={{ borderColor: "var(--ah-fg)", opacity: 0.4 }}
      data-testid="public-page-empty-blocks"
    >
      <p className="font-bold text-sm ah-muted">This page doesn&apos;t have any blocks yet.</p>
    </div>
  ) : (
    <>
      {visibleBlocks.map((b) => (
        <div key={b.block_id} data-block-anchor={b.block_id}>
          <ActionBlockRenderer
            block={b}
            onClick={trackClick}
            onGateUnlock={handleGateUnlock}
            hubContext={hubContext}
          />
        </div>
      ))}
      {gateBlock && !gateUnlocked && allBlocks.length > gateIdx + 1 && (
        <p className="text-center text-xs ah-muted italic pt-2"
           data-testid="public-page-gate-hint">
          🔒 {allBlocks.length - gateIdx - 1} more block{allBlocks.length - gateIdx - 1 === 1 ? "" : "s"} unlock after you complete the step above.
        </p>
      )}
    </>
  );

  return (
    <div className={`ah-theme ah-${theme} ah-root flex flex-col ${blockLayout ? "min-h-screen" : ""}`}
         data-testid="public-page-root" data-theme={theme} data-layout={blockLayout ? "blocks" : "classic"}
         style={linksStyle}>
      {/* v146.33 — Dynamic SEO tags for the specific action page. Fed from
          /api/seo/meta/page/{creatorSlug}/{pageSlug} → JSON-LD WebPage
          with an author reference back to the creator's Person entity. */}
      <SeoHead
        endpoint={creatorSlug && pageSlug
          ? `/seo/meta/page/${encodeURIComponent(creatorSlug)}/${encodeURIComponent(pageSlug)}`
          : null}
        fallback={{
          title: page?.title ? `${page.title} · Linkola` : "Linkola",
          description: page?.description || undefined,
          image: page?.cover_image_url || undefined,
        }}
      />
      {/* v146.7 — Bumped from 2.5rem to 4.5rem: PublicPageOverlay now paints
          a solid app-bar background (~3.75rem + safe-area) behind its
          back/name/share pills instead of leaving the strip transparent.
          At the old 2.5rem padding the avatar's top edge sat underneath
          that opaque bar and got clipped on load. */}
      {blockLayout ? (
        /* The Web Link's column and spacing (`PublicCreatorDirectory`). */
        <div className="max-w-3xl w-full mx-auto flex-1 px-4 sm:px-8 pb-4 sm:pb-8 pt-[calc(5rem+env(safe-area-inset-top,0))] sm:pt-24">
          {!page?.settings?.hide_engagement_bar && (
            <div className="mb-4 flex justify-center">
              <HubEngagementBar creatorSlug={creatorSlug} />
            </div>
          )}
          <div className="space-y-4" data-testid="public-page-blocks">
            {blockList}
          </div>
        </div>
      ) : (
      <div className="max-w-md w-full mx-auto flex-1 px-5 pt-[calc(4.5rem+env(safe-area-inset-top,0))] pb-16">
        {/* Creator header */}
        <div className="flex flex-col items-center text-center gap-3">
          {creator?.picture ? (
            <img
              src={creator.picture}
              alt=""
              className="w-20 h-20 rounded-full border-2 object-cover"
              style={{ borderColor: "var(--ah-fg)" }}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              data-testid="public-page-avatar"
            />
          ) : (
            <div className="w-20 h-20 rounded-full border-2" style={{ borderColor: "var(--ah-fg)", background: "var(--ah-accent)" }} />
          )}
          <div>
            <p className="ah-title text-2xl" data-testid="public-page-creator">
              {creator?.name || `@${creatorSlug}`}
            </p>
          </div>
        </div>

        {/* v96 — Engagement bar (Like / Follow / Message) shown on every
            action page by default so visitors can interact with the creator
            directly from any page. Same component as the Bio Hub for
            consistency. Creators can hide via page.settings.hide_engagement_bar. */}
        {!page?.settings?.hide_engagement_bar && (
          <div className="mt-4 flex justify-center">
            <HubEngagementBar creatorSlug={creatorSlug} />
          </div>
        )}

        {/* Page title / description */}
        <h1 className="ah-title mt-8 text-center text-2xl sm:text-3xl" data-testid="public-page-title">
          {page.title}
        </h1>
        {page.description && (
          <p className="mt-2 text-center text-sm ah-muted" data-testid="public-page-description">
            {page.description}
          </p>
        )}

        {/* Blocks */}
        <div className="mt-8 space-y-3">
          {blockList}
        </div>
      </div>
      )}

      {/* Footer */}
      <div className="py-4 text-center" style={{ borderTop: "1px solid var(--ah-fg)", opacity: 0.7 }}>
        <Link
          to="/"
          data-testid="public-page-footer-link"
          className="inline-flex items-center gap-2 text-xs font-bold ah-muted"
        >
          <Zap className="w-3.5 h-3.5" strokeWidth={3} />
          Built with Linkola
        </Link>
      </div>

      {/* Floating overlay: Join (left) + Share (right) */}
      <PublicPageOverlay
        creatorSlug={creatorSlug}
        pageSlug={pageSlug}
        pageTitle={page?.title}
      />
    </div>
  );
}
